import {
  Box,
  Flex,
  Text,
  Stack,
  Avatar,
  Tooltip,
  useColorModeValue,
  IconButton,
  Collapse,
} from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckCircleIcon,
  CheckIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import SubQuestIcon from 'components/subQuest/SubQuestIcon';
import { format, parseJSON } from 'date-fns';
import { useState } from 'react';
import ProgressDisplay from 'components/course/ProgressDisplay';

function ClassCourseItem({ records, course, userProgressions }) {
  const [isToggled, setIsToggled] = useState(true);

  function getQuizScoreDisplay(quizObj, quizScore) {
    if (quizObj.scoreType !== 'Actual Score') {
      if (quizObj.totalMark === 0) return `${parseFloat(quizScore).toFixed(2)}`;
      return `${parseFloat((quizScore / quizObj.totalMark) * 100).toFixed(2)}%`;
    }
    return `${parseFloat(quizScore.toFixed(2))}/${quizObj.totalMark}`;
  }

  const boxColor = useColorModeValue('#f7f7f7', 'gray.800');
  const typeColor = useColorModeValue('teal', 'white');
  const checkIconColor = useColorModeValue('green', 'white');

  const getCourseProgress = () => {
    const courseQuests = records.courseQuests.filter(
      (cq) => cq.course.id === course.id
    );

    const questSubQuests = records.questSubQuests.filter((qs) =>
      courseQuests.some((cq) => cq.quest.id === qs.quest.id)
    );

    const totalSubQuests = questSubQuests.length;

    const completedSubQuests = userProgressions.filter((up) =>
      questSubQuests.some(
        (sq) => sq.subQuest.id === up.subQuestId && up.dateFinished
      )
    ).length;

    return totalSubQuests > 0 ? (completedSubQuests / totalSubQuests) * 100 : 0;
  };

  return (
    <Box mb={6} borderWidth="1px" borderRadius="md">
      <Box w="100%" p={4} bg={boxColor}>
        <Flex align="center">
          <IconButton
            onClick={() => setIsToggled(!isToggled)}
            variant="plain"
            aria-label={isToggled ? 'Untoggle course' : 'Toggle course'}
            icon={isToggled ? <ArrowUpIcon /> : <ArrowDownIcon />}
          />

          <Box flex={1}>
            <Text fontWeight={500} mb={2} fontSize="md">
              {course.name}
            </Text>
            <ProgressDisplay
              percentage={getCourseProgress()}
              count={0}
              countLabel=""
              flex={1}
            />
          </Box>
        </Flex>
      </Box>

      <Collapse in={isToggled}>
        <Box p={4}>
          {records.courseQuests
            .filter((cq) => cq.course.id === course.id)
            .sort((a, b) => a.orderNo - b.orderNo)
            .map((courseQuest) => (
              <Box key={uuidv4()}>
                {records.questSubQuests
                  .filter((qs) => qs.quest.id === courseQuest.quest.id)
                  .sort((a, b) => a.orderNo - b.orderNo)
                  .map((questSubQuest) => {
                    const userProgress = userProgressions.find(
                      (up) => up.subQuestId === questSubQuest.subQuest.id
                    );
                    return (
                      <Box key={uuidv4()} w="100%" p={4} mb={6} bg={boxColor}>
                        <Flex align="center">
                          <SubQuestIcon
                            typeId={questSubQuest.subQuest.type.id}
                            boxSize={7}
                            mx={4}
                            color={typeColor}
                          />

                          <Stack flex={1}>
                            <Text fontWeight={500} fontSize="sm">
                              {questSubQuest.subQuest.name}
                            </Text>

                            {userProgress && (
                              <Box>
                                {userProgress.dateFinished && (
                                  <Text fontSize="xs">
                                    Completed:&nbsp;
                                    {format(
                                      parseJSON(userProgress.dateFinished),
                                      'MMMM d, yyyy (h:mm a)'
                                    )}
                                  </Text>
                                )}

                                {/* Quiz Result */}
                                {userProgress.quizResult && (
                                  <Text fontSize="sm" mb={1}>
                                    Score:&nbsp;
                                    <Text as="span" fontWeight={500}>
                                      {getQuizScoreDisplay(
                                        questSubQuest.subQuest.subQuestQuiz,
                                        userProgress.quizScore
                                      )}
                                      &nbsp;
                                      <Tooltip
                                        label={
                                          userProgress.quizResult === 'Pass'
                                            ? 'Passed'
                                            : 'Failed'
                                        }
                                        fontSize="md"
                                      >
                                        <Avatar
                                          size="2xs"
                                          bg={
                                            userProgress.quizResult === 'Pass'
                                              ? 'green'
                                              : 'red'
                                          }
                                          icon={
                                            userProgress.quizResult ===
                                            'Pass' ? (
                                              <CheckIcon boxSize="2" />
                                            ) : (
                                              <CloseIcon boxSize="2" />
                                            )
                                          }
                                          mr={2}
                                        />
                                      </Tooltip>
                                    </Text>
                                  </Text>
                                )}

                                {/* Interactive Result */}
                                {userProgress.interactiveContentScore && (
                                  <Text fontSize="sm" mb={1}>
                                    Score:&nbsp;
                                    <Text as="span" fontWeight={500}>
                                      {parseFloat(
                                        userProgress.interactiveContentScore.toFixed(
                                          2
                                        )
                                      )}
                                    </Text>
                                  </Text>
                                )}
                              </Box>
                            )}
                          </Stack>

                          {userProgress && userProgress.dateFinished && (
                            <Tooltip label="Completed">
                              <CheckCircleIcon
                                boxSize={5}
                                color={checkIconColor}
                              />
                            </Tooltip>
                          )}
                        </Flex>
                      </Box>
                    );
                  })}
              </Box>
            ))}
        </Box>
      </Collapse>
    </Box>
  );
}

ClassCourseItem.propTypes = {
  records: PropTypes.object.isRequired,
  course: PropTypes.object.isRequired,
  userProgressions: PropTypes.array.isRequired,
};

export default ClassCourseItem;
